import React, { useMemo } from 'react';
import { Box, Button, Chip, Stack, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import {
  ChatBubbleOutline as CommentsIcon,
  FitnessCenter as FitnessIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import { format, formatDistanceToNowStrict } from 'date-fns';
import { SuccessNominationRecord } from '../nomination.types';
import { normalizeClientName } from '../utils/nominationFilters';

export interface NominationsTableProps {
  nominations: SuccessNominationRecord[];
  onRowClick: (nomination: SuccessNominationRecord) => void;
  onOpenComments: (nomination: SuccessNominationRecord) => void;
  onOpenHistory: (nomination: SuccessNominationRecord) => void;
  /** Show the location under the client name (admins span multiple locations). */
  showLocation?: boolean;
  /** Test-only escape hatch: jsdom has no layout, so virtualised rows never render. */
  disableVirtualization?: boolean;
}

export interface NominationRow extends SuccessNominationRecord {
  client_display_name: string;
  created_at_ts: number;
  sessions_completed: number;
}

const ROLE_LABEL: Record<SuccessNominationRecord['nominated_by']['role'], string> = {
  USER: 'Coach',
  MANAGER: 'Manager',
  ADMIN: 'Admin',
};

const toRow = (nomination: SuccessNominationRecord): NominationRow => ({
  ...nomination,
  client_display_name: normalizeClientName(nomination.client_name) || nomination.client_name,
  created_at_ts: nomination.created_at.getTime(),
  sessions_completed: nomination.training.sessionsCompleted,
});

/**
 * Sortable nominations grid. Every column maps to a flat, primitive row field so
 * DataGrid's built-in sorting works without custom comparators.
 */
export const NominationsTable: React.FC<NominationsTableProps> = ({
  nominations,
  onRowClick,
  onOpenComments,
  onOpenHistory,
  showLocation = false,
  disableVirtualization = false,
}) => {
  const theme = useTheme();
  const rows = useMemo(() => nominations.map(toRow), [nominations]);

  const columns: GridColDef<NominationRow>[] = useMemo(
    () => [
      {
        field: 'client_display_name',
        headerName: 'Client',
        flex: 1.6,
        minWidth: 220,
        renderCell: (params: GridRenderCellParams<NominationRow>) => (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {params.row.client_display_name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              ID {params.row.client_id}
              {showLocation && params.row.client_location ? ` · ${params.row.client_location}` : ''}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'created_at_ts',
        headerName: 'Nominated',
        type: 'number',
        flex: 1,
        minWidth: 150,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params: GridRenderCellParams<NominationRow>) => (
          <Box>
            <Typography variant="body2">{format(params.row.created_at, 'd MMM yyyy')}</Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDistanceToNowStrict(params.row.created_at, { addSuffix: true })}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'nominated_by_name',
        headerName: 'Nominated by',
        flex: 1.1,
        minWidth: 170,
        renderCell: (params: GridRenderCellParams<NominationRow>) => (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {params.row.nominated_by_name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {ROLE_LABEL[params.row.nominated_by.role]}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'sessions_completed',
        headerName: 'Sessions completed',
        type: 'number',
        flex: 0.9,
        minWidth: 160,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params: GridRenderCellParams<NominationRow>) => {
          const { training } = params.row;
          return (
            <Tooltip
              title={`${training.totalVisits} total visits · ${training.noShows} no-shows · ${training.lateCancels} late cancels`}
            >
              <Chip
                icon={<FitnessIcon fontSize="small" />}
                label={training.sessionsCompleted}
                size="small"
                variant="outlined"
                color="primary"
                sx={{ fontWeight: 600, minWidth: 64 }}
              />
            </Tooltip>
          );
        },
      },
      {
        field: 'actions',
        headerName: 'Review',
        width: 230,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params: GridRenderCellParams<NominationRow>) => (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<CommentsIcon fontSize="small" />}
              onClick={event => {
                event.stopPropagation();
                onOpenComments(params.row);
              }}
              aria-label={`Open comments for ${params.row.client_display_name}`}
            >
              Comments
            </Button>
            <Button
              size="small"
              variant="text"
              startIcon={<HistoryIcon fontSize="small" />}
              onClick={event => {
                event.stopPropagation();
                onOpenHistory(params.row);
              }}
              aria-label={`Open success history for ${params.row.client_display_name}`}
            >
              History
            </Button>
          </Stack>
        ),
      },
    ],
    [onOpenComments, onOpenHistory, showLocation]
  );

  return (
    <DataGrid
      rows={rows}
      columns={columns}
      getRowId={row => row.id}
      getRowHeight={() => 'auto'}
      onRowClick={(params: GridRowParams<NominationRow>) => onRowClick(params.row)}
      initialState={{
        pagination: { paginationModel: { pageSize: 25 } },
        sorting: { sortModel: [{ field: 'created_at_ts', sort: 'desc' }] },
      }}
      pageSizeOptions={[10, 25, 50, 100]}
      disableRowSelectionOnClick
      disableVirtualization={disableVirtualization}
      autoHeight
      localeText={{ noRowsLabel: 'No nominations match the current filters' }}
      sx={{
        border: 'none',
        '& .MuiDataGrid-cell': {
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
        },
        '& .MuiDataGrid-columnHeaders': {
          backgroundColor: alpha(theme.palette.primary.main, 0.06),
          borderBottom: `2px solid ${theme.palette.primary.main}`,
        },
        '& .MuiDataGrid-row': {
          cursor: 'pointer',
          '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.03) },
        },
        '& .MuiDataGrid-footerContainer': {
          borderTop: `2px solid ${theme.palette.divider}`,
        },
      }}
    />
  );
};

export default NominationsTable;
